import { useState, type ReactNode } from 'react';
import type { MergeCandidate } from './mergeCandidates';

// 11e — a merge is destructive and someone will question it in three months.
// So: evidence per candidate, an irreversibility line, an audit note that is
// actually persisted, and a button that NAMES THE TARGET.
//
// The artboard's second consequence — "both secondary emails become aliases,
// so replies to either still thread here" — is NOT rendered. This backend does
// not alias anything: it marks the source `status: 'merged'` and leaves its
// email on the row. Promising threading it does not do would be the worst kind
// of lie on a dialog whose whole job is to be believed. What it does do is
// stated instead, in warn, because it is a consequence worth knowing.

const MONO = 'font-mono';

export function MergeDialog({
  candidates,
  busy,
  onCancel,
  onMerge,
}: {
  candidates: MergeCandidate[];
  busy: boolean;
  onCancel: () => void;
  onMerge: (primaryId: string, mergedIds: string[], note: string) => void;
}) {
  const [primaryId, setPrimaryId] = useState(
    () => (candidates.find((c) => c.isRichest) ?? candidates[0])?.id,
  );
  const [note, setNote] = useState('');

  const primary = candidates.find((c) => c.id === primaryId) ?? candidates[0];
  const merged = candidates.filter((c) => c.id !== primary.id);
  const tickets = merged.reduce((n, c) => n + c.ticketCount, 0);
  const activities = merged.reduce((n, c) => n + c.activityCount, 0);

  return (
    <div
      className="w-[860px] max-w-[94vw] bg-[var(--co-raised)]"
      style={{ boxShadow: 'var(--co-shadow-pop)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Merge duplicate records"
    >
      <div className="flex items-start justify-between gap-4 px-6 pt-5">
        <div className="min-w-0">
          <div className={`${MONO} text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}>
            {`MERGE ${candidates.length} RECORDS INTO ONE`}
          </div>
          <h2
            className="mt-2 truncate text-[24px] leading-[1.15] text-[var(--co-ink)]"
            style={{ fontFamily: 'var(--co-font-display)' }}
          >
            {primary.email}
          </h2>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel merge"
          className="flex-none text-[14px] text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
        >
          ✕
        </button>
      </div>

      <div className="mx-6 mt-[18px] h-px bg-[var(--co-line)]" />

      <div className="max-h-[62vh] overflow-y-auto px-6 pt-[18px]">
        <div className={`${MONO} text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}>
          KEEP AS PRIMARY
        </div>

        <div className="mt-3 flex flex-col gap-2" role="radiogroup" aria-label="Primary record">
          {candidates.map((c) => {
            const on = c.id === primary.id;
            return (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setPrimaryId(c.id)}
                className="flex items-center gap-[14px] border px-[15px] py-[13px] text-left"
                style={{
                  borderColor: on ? 'var(--co-ink)' : 'var(--co-line)',
                  background: on ? 'var(--co-brass-wash)' : 'var(--co-page)',
                }}
              >
                <span
                  className="flex h-[15px] w-[15px] flex-none items-center justify-center border text-[9px]"
                  style={{
                    borderColor: on ? 'var(--co-ink)' : 'var(--co-line-strong)',
                    background: on ? 'var(--co-ink)' : 'transparent',
                    color: 'var(--co-ink-invert)',
                  }}
                  aria-hidden="true"
                >
                  {on ? '✓' : ''}
                </span>

                <span className="min-w-0 flex-1">
                  <span
                    className="block truncate text-[13.5px]"
                    style={{ fontWeight: on ? 600 : 400, color: on ? 'var(--co-ink)' : 'var(--co-ink-2)' }}
                  >
                    {c.email}
                  </span>
                  {/* The meta line carries the EVIDENCE, not just counts —
                      without it the dialog is asking for a guess. */}
                  <span
                    className={`${MONO} mt-1 block truncate text-[10.5px] tracking-[.08em] text-[var(--co-ink-4)]`}
                  >
                    {[
                      `${c.ticketCount} TICKETS`,
                      `${c.activityCount} ACTIVITIES`,
                      `SINCE ${c.since.toUpperCase()}`,
                      c.organisation?.toUpperCase(),
                      c.evidence,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>

                <span
                  className={`${MONO} flex-none text-[9.5px] tracking-[.1em]`}
                  style={{
                    color: on ? 'var(--co-ink)' : 'var(--co-ink-3)',
                    fontWeight: on ? 600 : 500,
                  }}
                >
                  {c.isRichest && on ? 'RICHEST RECORD' : on ? 'PRIMARY' : 'WILL BE MERGED'}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-5 bg-[var(--co-page)] px-[15px] py-[14px]">
          <div className={`${MONO} text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}>
            WHAT HAPPENS
          </div>
          <div className="mt-3 flex flex-col gap-2 text-[12.5px] leading-[1.5]">
            <Consequence tone="met">
              {`${tickets} ticket${tickets === 1 ? '' : 's'}, ${activities} ${
                activities === 1 ? 'activity' : 'activities'
              }, and all notes, contacts and tags move onto ${primary.email}.`}
            </Consequence>
            <Consequence tone="warn">
              {`${merged
                .map((c) => c.email)
                .join(' and ')} ${merged.length === 1 ? 'is' : 'are'} marked merged and kept as an audit record — not deleted. Replies sent to ${
                merged.length === 1 ? 'that address' : 'those addresses'
              } do NOT thread onto the primary.`}
            </Consequence>
            <Consequence tone="breach">
              Merges cannot be undone. Where fields conflict, the primary&rsquo;s value is kept.
            </Consequence>
          </div>
        </div>

        {/* A merge is a destructive act someone will question in three months. */}
        <label className="mt-5 block">
          <span className={`${MONO} text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}>
            NOTE FOR THE AUDIT TRAIL · OPTIONAL
          </span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Same person — confirmed by phone with the customer on 8 Sep"
            className="mt-2 h-[32px] w-full border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-[10px] text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-4)] focus:border-[var(--co-ink)] focus:outline-none"
          />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-[var(--co-line)] bg-[var(--co-sunken)] px-6 py-[14px]">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex h-[34px] items-center border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-ink)] disabled:opacity-50"
        >
          Cancel
        </button>
        {/* Names the target: the last chance to notice the primary is wrong.
            `Merge` alone gives none. */}
        <button
          type="button"
          onClick={() => onMerge(primary.id, merged.map((c) => c.id), note)}
          disabled={busy || merged.length === 0}
          className="flex h-[34px] max-w-full items-center truncate px-4 text-[12.5px] font-semibold disabled:cursor-not-allowed"
          style={{
            background: busy || merged.length === 0 ? 'var(--co-line-strong)' : 'var(--co-ink)',
            color: busy || merged.length === 0 ? 'var(--co-raised)' : 'var(--co-ink-invert)',
          }}
        >
          {busy ? 'Merging…' : `Merge into ${primary.email}`}
        </button>
      </div>
    </div>
  );
}

function Consequence({ tone, children }: { tone: 'met' | 'warn' | 'breach'; children: ReactNode }) {
  const color =
    tone === 'met'
      ? 'var(--co-green-ink)'
      : tone === 'warn'
        ? 'var(--co-terracotta-ink)'
        : 'var(--co-burgundy)';
  return (
    <div className="flex items-start gap-2">
      <span className={`${MONO} flex-none text-[11px]`} style={{ color }} aria-hidden="true">
        →
      </span>
      <span style={{ color: tone === 'met' ? 'var(--co-ink-2)' : color }}>{children}</span>
    </div>
  );
}

export default MergeDialog;
